import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CoinMascot, Confetti } from "@/components/brand/CoinMascot";
import { Logo } from "@/components/brand/Logo";
import { BUSINESS } from "@/components/setup/BusinessSteps";
import { PERSONAL } from "@/components/setup/PersonalSteps";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useActiveCompany } from "@/lib/activeCompany";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";

export default function Setup() {
  const [params] = useSearchParams();
  const space = params.get("space") === "business" ? "business" : "personal";
  const DEF = space === "business" ? BUSINESS : PERSONAL;
  const { user } = useAuth();
  const { active } = useHousehold();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [p, setP] = useState(null);
  const [step, setStep] = useState(0);
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const cid = useActiveCompany()?.id;
  const ctx = { hid: active?.id, cid, answers: p?.answers || {} };

  useEffect(() => {
    api.post(`/setup/${space}/start`).then(() => api.get(`/setup/${space}`)).then(({ data }) => {
      setP(data);
      qc.setQueryData(["setup", space], data);
      const i = data.next ? data.steps.indexOf(data.next) : data.steps.length;
      setStep(i);
      if (data.next) setV(data.answers[data.next] || {});
    }).catch((e) => toast.error(formatApiError(e)));
  }, [space, qc]);

  if (space === "business" && !cid) return <Navigate to="/entreprise" replace />;
  if (!p) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const key = p.steps[step];
  const S = DEF[key];
  const done = step >= p.steps.length;
  const home = space === "business" ? "/entreprise" : "/";
  const go = (i) => { setStep(i); setV(p.answers[p.steps[i]] || {}); };
  const exit = () => { qc.invalidateQueries(); navigate(home); };

  const save = async (connect = false) => {
    setBusy(true);
    try {
      if (S.apply) await S.apply(v, ctx);
      let url = null;
      if (connect) url = await S.connect(!!user.is_demo, ctx);
      const { data } = await api.put(`/setup/${space}/${key}`, { data: { ...v, connected: connect || v.connected || false }, household_id: active?.id });
      setP(data);
      if (url) { window.location.assign(url); return; }
      if (connect) toast.success(user.is_demo ? "Banque de démonstration connectée (simulée)" : "Banque connectée");
      go(step + 1);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col" data-testid="setup-page">
      <header className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 pt-6">
        <Logo size={32} />
        <button onClick={exit} className="text-sm text-muted-foreground hover:text-foreground" data-testid="setup-later-button">Reprendre plus tard</button>
      </header>
      <div className="mx-auto mt-6 flex w-full max-w-2xl gap-2 px-6" data-testid="setup-progress">
        {p.steps.map((s, i) => <button key={s} onClick={() => go(i)} aria-label={DEF[s].title} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${p.completed.includes(s) ? "bg-sage" : i === step ? "bg-sage/50" : "bg-secondary"}`} />)}
      </div>
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 pb-32 pt-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            {done ? (
              <div className="flex flex-col items-center pt-10 text-center" data-testid="setup-done">
                <Confetti /><CoinMascot mood="celebrate" size={120} />
                <h1 className="mt-8 text-3xl font-extrabold sm:text-4xl">{p.done ? "Configuration terminée !" : "Presque terminé !"}</h1>
                <p className="mt-3 max-w-md text-muted-foreground">{p.count}/{p.total} étapes complétées. {p.done ? "Vous pouvez modifier vos réponses à tout moment depuis l'accueil." : "Reprenez les étapes restantes quand vous voulez depuis la carte « Configuration » de l'accueil."}</p>
                <Button className="mt-8 h-12 rounded-full px-8" onClick={exit} data-testid="setup-finish-button">Voir mon tableau de bord<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            ) : (
              <>
                <div className="eyebrow">{space === "business" ? "Entreprise" : "Particulier & foyer"} · étape {step + 1} sur {p.steps.length}</div>
                <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-testid="setup-step-title">{S.title}</h1>
                <p className="mt-2 text-muted-foreground">{S.text}</p>
                <div className="mt-8"><S.Comp v={v} set={(patch) => setV((s) => ({ ...s, ...patch }))} demo={!!user.is_demo} /></div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
      {!done && (
        <footer className="glass fixed inset-x-0 bottom-0 border-t safe-bottom">
          <div className="mx-auto flex max-w-2xl flex-wrap items-center gap-2 px-6 py-4">
            {step > 0 && <Button variant="ghost" className="rounded-full" onClick={() => go(step - 1)} data-testid="setup-back-button"><ArrowLeft className="mr-1 h-4 w-4" />Retour</Button>}
            {S.bank ? <>
              <Button variant="outline" className="ml-auto rounded-full" disabled={busy} onClick={() => go(step + 1)} data-testid="setup-skip-button">Plus tard</Button>
              <Button className="h-11 rounded-full px-6" disabled={busy} onClick={() => save(true)} data-testid="setup-connect-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Landmark className="mr-2 h-4 w-4" />}Connecter</Button>
            </> : <Button className="ml-auto h-11 rounded-full px-6" onClick={() => save()} disabled={busy} data-testid="setup-next-button">Continuer<ArrowRight className="ml-2 h-4 w-4" /></Button>}
          </div>
        </footer>
      )}
    </div>
  );
}
